import React from 'react';
import { APP } from '../tokens';
import type { ReviewStats } from '../../../types/review';

const F = APP.fontFamily;

interface ReviewSummaryStripProps {
  stats: ReviewStats;
}

export default function ReviewSummaryStrip({ stats }: ReviewSummaryStripProps) {
  const progressPct = stats.total > 0 ? (stats.resolved / stats.total) * 100 : 0;
  const isComplete = stats.total > 0 && stats.remaining === 0;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        paddingBlock: 14,
        paddingInline: 16,
        gap: 10,
        borderBottom: `1px solid ${APP.borderReviewCardDefault}`,
      }}
    >
      {/* Stats headline */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: APP.textDark, lineHeight: '16px', fontFamily: F }}>
          {stats.total} suggestions
        </span>
        <span style={{ fontSize: 12, fontWeight: 400, color: APP.textReviewMuted, lineHeight: '16px', fontFamily: F }}>·</span>
        {isComplete ? (
          <span style={{ fontSize: 12, fontWeight: 500, color: APP.textReviewAccepted, lineHeight: '16px', fontFamily: F }}>
            All reviewed
          </span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: APP.textReviewPriorityHigh, lineHeight: '16px', fontFamily: F }}>
            {stats.highPriority} high priority
          </span>
        )}
      </div>

      {/* Progress bar + resolved counters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* Bar */}
        <div
          style={{
            width: '100%',
            height: 4,
            borderRadius: 2,
            backgroundColor: APP.bgReviewProgress,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: `${progressPct}%`,
              height: 4,
              borderRadius: 2,
              backgroundColor: isComplete ? APP.textReviewAccepted : APP.textReviewPriorityHigh,
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* Resolved counters */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, fontWeight: 400, color: APP.textReviewMuted, lineHeight: '12px', fontFamily: F }}>
            {stats.resolved} of {stats.total} reviewed
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 400, color: APP.textReviewAccepted, lineHeight: '12px', fontFamily: F }}>
              {stats.accepted} accepted
            </span>
            <span style={{ fontSize: 10, fontWeight: 400, color: APP.textReviewMuted, lineHeight: '12px', fontFamily: F }}>
              {stats.dismissed} dismissed
            </span>
          </div>
        </div>
      </div>

      {/* Category pills — only shown when review is in progress */}
      {!isComplete && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {stats.rewrites > 0 && (
            <span style={pillStyle}>{stats.rewrites} rewrites</span>
          )}
          {stats.additions > 0 && (
            <span style={pillStyle}>{stats.additions} additions</span>
          )}
          {stats.deletions > 0 && (
            <span style={pillStyle}>{stats.deletions} deletions</span>
          )}
        </div>
      )}
    </div>
  );
}

const pillStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 10,
  paddingBlock: 2,
  paddingInline: 8,
  backgroundColor: APP.bgReviewCategoryPill,
  fontSize: 9,
  fontWeight: 500,
  color: APP.textReviewInfo,
  lineHeight: '12px',
  fontFamily: APP.fontFamily,
};
